import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Columns from "pages/TalentAgencies/column";
import CommonTable from "components/Tables/CommonTable";
import { CompaniesWrapperStyles } from "pages/Companies/styled";
import Input from "components/Input/index";
import LoadingSpinner from "components/PageLoader/Spinner";

import { CommonDashboardWrapper } from "styles/GlobalStyles";
import { useTalentAgenciesSlice } from "./slice/index";
import Icon from "components/Icon/Icon";
import { listTalentAgencySelector } from "./slice/selector";

import {
  MagnifyingGlassIcon,
  XIcon,
  XCircleIcon,
  CaretDownIcon
} from "@phosphor-icons/react";
import PillButtons from "components/Buttons/PillButtons";

const TalentAgencies = () => {
  const dispatch = useDispatch();
  const { actions } = useTalentAgenciesSlice();
  const { loading, agencies, pagination, total_agencies, leads_opted_counts } =
    useSelector(listTalentAgencySelector);

  const navigate = useNavigate();
  const location = useLocation();

  // --- Parse initial query params from URL ---
  const queryParams = new URLSearchParams(location.search);
  const initialPage = Number(queryParams.get("page")) || 1;
  const initialLimit = Number(queryParams.get("limit")) || 10;
  const initialSearch = queryParams.get("search") || "";
  const initialStatus = queryParams.get("leads_opted_status") || "";

  // --- Local states ---
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const [debouncedSearch, setDebouncedSearch] = useState(initialSearch);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [selectedStatus, setSelectedStatus] = useState(initialStatus);

  const statusMenuItems = [
    { label: "All", value: "" },
    {
      label: "Opted In",
      value: "opted_in",
      count: leads_opted_counts?.opted_in
    },
    {
      label: "Opted Out",
      value: "opted_out",
      count: leads_opted_counts?.opted_out
    }
  ];

  const selectedStatusLabel =
    statusMenuItems.find((item) => item.value === selectedStatus)?.label || "";

  // --- Debounce search input ---
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // --- Reset to page 1 only when user types new search ---
  useEffect(() => {
    // Only reset if search actually changed (not on initial mount)
    if (debouncedSearch !== initialSearch) {
      setCurrentPage(1);
    }
  }, [debouncedSearch]);

  // --- Sync URL and fetch data when page/search changes ---
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearch) params.set("search", debouncedSearch);
    if (selectedStatus) params.set("leads_opted_status", selectedStatus);
    params.set("page", currentPage);
    params.set("limit", initialLimit);

    // Update the browser URL (so refresh preserves page & search)
    navigate(`?${params.toString()}`, { replace: false });

    // Trigger API fetch
    dispatch(
      actions.fetchTalentAgencyList({
        page: currentPage,
        limit: initialLimit,
        search: debouncedSearch,
        leads_opted_status: selectedStatus
      })
    );
  }, [debouncedSearch, currentPage, selectedStatus]);

  const tableColumns = useMemo(() => Columns(), []);

  const handleStatusSelect = (item) => {
    setSelectedStatus(item.value);
    setCurrentPage(1); // reset page on filter change
  };

  // --- Clear search ---
  const handleClearSearch = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setSelectedStatus("");
    setCurrentPage(1);
  };

  // --- Page change handler ---
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  return (
    <CompaniesWrapperStyles>
      <CommonDashboardWrapper className="common-dashboard-talent-agency-wrapper">
        <div className="common-dashboard-wrapper">
          <h1 className="common-heading">Talent Agencies</h1>
        </div>
      </CommonDashboardWrapper>

      <div className="talent-agency-table-wrapper">
        <div className="filter-bars">
          <PillButtons
            placeholderText="Opted Leads"
            statusPlaceholder="statusActive"
            mainLabelText={selectedStatusLabel}
            rightIcon={
              <Icon
                component={CaretDownIcon}
                format="outline"
                weight="regular"
              />
            }
            activeRightIcon={
              <Icon component={XCircleIcon} format="outline" weight="regular" />
            }
            menuItems={statusMenuItems}
            onSelect={handleStatusSelect}
            size="M"
            maxWidth="max-content"
            customClassName="status-width"
          />

          <Input
            id="searchCompany"
            type="search"
            placeholder="Search by Agency Name"
            leftIcon={
              <Icon
                component={MagnifyingGlassIcon}
                format="outline"
                weight="regular"
              />
            }
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="S"
            className="search-input"
            rightIcon={
              loading ? (
                <LoadingSpinner color="#1f1f1f" size={12} />
              ) : (
                <Icon component={XIcon} format="outline" weight="regular" />
              )
            }
            handleClearSearch={handleClearSearch}
          />
        </div>

        <CommonTable
          data={agencies}
          columns={tableColumns}
          tableClassName="talent-agencies-table"
          tableWrapperStyles="table-wrapper-styles"
          loading={loading}
          pagination={pagination}
          total_count={total_agencies}
          page={currentPage}
          limit={initialLimit}
          onPageChange={handlePageChange}
        />
      </div>
    </CompaniesWrapperStyles>
  );
};

export default TalentAgencies;
